import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiClient, storePath } from '@/lib/api-client';
import type { PublishOptions, StorePlatform, StoreScreenshot } from '@/types/store';

import { storeKeys } from './store-keys';

export interface PublishScreenshotsVars {
  platform: StorePlatform;
  locale: string;
  options?: PublishOptions;
}

/**
 * POST …/screenshots/:platform/:locale/publish — fetch every saved screenshot
 * from R2 and upload it to the store. Returns the published rows. A 503 (store
 * credentials not configured) surfaces as an `ApiError` for the caller.
 */
export function usePublishScreenshots(): UseMutationResult<
  StoreScreenshot[],
  ApiError,
  PublishScreenshotsVars
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ platform, locale, options }) =>
      apiClient.post<StoreScreenshot[]>(
        storePath(
          `/screenshots/${platform}/${encodeURIComponent(locale)}/publish`,
          options?.changesNotSentForReview ? { changesNotSentForReview: true } : undefined,
        ),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: storeKeys.all });
    },
  });
}
